import React, {useCallback, useMemo, useState} from 'react';
import {Box, Button, ButtonGroup, Grid} from "@material-ui/core";
import MaterialTable, {Action, Column} from "material-table";
import {PaginationProps, SimpleTablePagination} from "../../components/table/pagination/SimpleTablePagination";
import {tableIcons} from "../../components/utils/material-table";
import {UserRow, useUserLoader} from "./hooks/useUserLoader";
import {useClients} from "../../hooks/useClients";
import _ from "lodash";
import {Icon} from "../../components/icon/Icon";
import {AddClientDialog} from "./dialogs/create/AddClientDialog";
import {useBoolean} from "../../hooks/utils/useBoolean";
import {EditClientDialog} from "./dialogs/edit/EditClientDialog";
import { ConfirmResetPasswordEmail } from './dialogs/edit/ConfirmResetPasswordEmail';
import { ConfirmManualResetPasswordEmail } from './dialogs/edit/ConfirmManualResetPasswordEmail';


export const AdminPage = () => {
  const [isAddClient, openAddClient, closeAddClient] = useBoolean(),
    [isEditClient, openEditClient, closeEditClient] = useBoolean(),
    [isResetPassword, openResetPasswordConfirmation, closeResetPasswordConfirmation] = useBoolean(),
    [isManualResetPassword, openManualResetPasswordConfirmation, closeManualResetPasswordConfirmation] = useBoolean(),
    [activeUser, setActiveUser] = useState<UserRow>()

  const {loader, isLastPage} = useUserLoader(),
    {clients, loadClientById} = useClients()

  const tableHeaders: Column<UserRow>[] = useMemo( () => {
    return [
      {
        title: '#',
        field: 'id',
        filtering: false
      },
      {
        title: 'Username',
        field: 'email',
        filtering: false
      },
      {
        title: 'Role',
        field: 'role',
        filtering: false
      },
    ]
  }, [] )

  const editClientAction = useCallback( (rowData: UserRow): Action<UserRow> => {
    let disabled = true
    const hidden = false
    let tooltip = undefined
    if (rowData.relatedEntityId) {
      const client = _.find(clients, {id: rowData.relatedEntityId})
      if (!client) {
        loadClientById(rowData.relatedEntityId).catch(console.error)
      } else {
        tooltip = 'Edit '+client.name
        disabled = false
      }
    }
    return {
      hidden,
      disabled,
      icon: () => <Icon icon='faPen' />,
      onClick: () => {
        setActiveUser(rowData)
        openEditClient()
      },
      tooltip,
      position: "row"
    }
  }, [clients, loadClientById, setActiveUser, openEditClient])

  const sendResetEmailAction = useCallback( (rowData: UserRow): Action<UserRow> => {
    const hidden = false,
      disabled = false,
      tooltip = "Send Reset Password Email";

    return {
      hidden,
      disabled,
      icon: () => <Icon icon='faEnvelope' />,
      onClick: () => {
        setActiveUser(rowData)
        openResetPasswordConfirmation()
      },
      tooltip,
      position: "row"
    }
  }, [setActiveUser, openResetPasswordConfirmation])

  const sendManualResetEmailAction = useCallback( (rowData: UserRow): Action<UserRow> => {
    const hidden = false,
      disabled = false,
      tooltip = "Send Manual Reset Password Email";

    return {
      hidden,
      disabled,
      icon: () => <Icon icon='faKey' />,
      onClick: () => {
        setActiveUser(rowData)
        openManualResetPasswordConfirmation()
      },
      tooltip,
      position: "row"
    }
  }, [setActiveUser, openManualResetPasswordConfirmation])

  return (
    <>
      <Box mt={2}>
        <ButtonGroup>
          <Button onClick={openAddClient}>
            Add user
          </Button>
        </ButtonGroup>
      </Box>
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MaterialTable<UserRow>
              title='Users'
              components={{
                Pagination: (p: PaginationProps) => <SimpleTablePagination isLastPage={isLastPage} {...p} />
              }}
              icons={tableIcons}
              columns={tableHeaders}
              options={{
                search: false,
                filtering: false,
                actionsColumnIndex: -1
              }}
              actions={[
                editClientAction,
                sendResetEmailAction,
                sendManualResetEmailAction
              ]}
              data={loader} />
          </Grid>
        </Grid>
      </Box>
      <AddClientDialog open={isAddClient} onClose={closeAddClient} />
      {activeUser && <EditClientDialog open={isEditClient} onClose={closeEditClient} activeUser={activeUser} />}
      {activeUser && <ConfirmResetPasswordEmail open={isResetPassword} onClose={closeResetPasswordConfirmation} activeUser={activeUser}/>}
      {activeUser && <ConfirmManualResetPasswordEmail open={isManualResetPassword} onClose={closeManualResetPasswordConfirmation} activeUser={activeUser}/>}
    </>
  );
};
